import React, { useState } from "react";
import { motion } from "framer-motion";
import { Send, Mail, Phone, MapPin, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const CONTACT_EMAIL = "info@fiscalbond.es";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Contact = () => {
  const [form, setForm] = useState({
    nombre: "",
    empresa: "",
    cargo: "",
    sector: "",
    email: "",
    telefono: "",
    mensaje: "",
    rgpd: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const handle = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === "checkbox" ? checked : value }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.rgpd) {
      toast.error("Debes aceptar la política de privacidad para continuar.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/contact`, form, { timeout: 15000 });
      if (data?.ok) {
        setSent(true);
        toast.success("Mensaje enviado correctamente.");
      } else {
        throw new Error("Respuesta inválida del servidor");
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || "No hemos podido enviar tu mensaje. Vuelve a intentarlo en unos minutos o escríbenos a info@fiscalbond.es.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setForm({ nombre: "", empresa: "", cargo: "", sector: "", email: "", telefono: "", mensaje: "", rgpd: false });
    setSent(false);
  };

  return (
    <section
      id="contacto"
      data-testid="contact-section"
      className="relative bg-[#0A1628] py-24 md:py-32 overflow-hidden"
    >
      <div className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full bg-[#0066FF]/15 blur-[140px]" />
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full bg-[#FF6B00]/10 blur-[120px]" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="lg:col-span-5"
        >
          <span className="text-xs uppercase tracking-[0.25em] text-[#FF6B00] font-semibold">
            Contacto
          </span>
          <h2
            className="mt-4 text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight"
            style={{ fontFamily: "Poppins" }}
          >
            Cuéntanos tu operativa y diseñamos tu solución.
          </h2>
          <p className="mt-5 text-slate-300 text-lg">
            Cada día sin una gestión fiscal optimizada es liquidez que tu
            empresa no aprovecha. Háblanos sin compromiso, te respondemos en
            menos de 24 horas.
          </p>

          <div className="mt-10 space-y-5">
            <a href={`mailto:${CONTACT_EMAIL}`} className="flex items-center gap-4 text-slate-200 hover:text-white group" data-testid="contact-email-link">
              <div className="w-12 h-12 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center group-hover:bg-[#0066FF] transition-colors">
                <Mail className="w-5 h-5" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-400">Email</div>
                <div className="font-medium">{CONTACT_EMAIL}</div>
              </div>
            </a>
            <div className="flex items-center gap-4 text-slate-200">
              <div className="w-12 h-12 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center">
                <Phone className="w-5 h-5" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-400">Teléfono</div>
                <div className="font-medium flex flex-wrap gap-x-3">
                  <a href="tel:+34926032626" className="hover:text-white" data-testid="contact-phone-1">926 032 626</a>
                  <span className="text-slate-500">/</span>
                  <a href="tel:+34633089026" className="hover:text-white" data-testid="contact-phone-2">633 089 026</a>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-4 text-slate-200">
              <div className="w-12 h-12 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center">
                <MapPin className="w-5 h-5" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-400">Sede</div>
                <div className="font-medium" data-testid="contact-sede">Alcázar de San Juan · Ciudad Real · España</div>
              </div>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="lg:col-span-7"
        >
          {sent ? (
            <div
              data-testid="contact-success"
              className="bg-gradient-to-br from-[#1E3A5F]/40 to-[#0A1628]/40 backdrop-blur-md border border-[#0066FF]/40 rounded-3xl p-8 md:p-12 text-center"
            >
              <div className="w-20 h-20 mx-auto rounded-full bg-[#0066FF]/15 border border-[#0066FF]/40 flex items-center justify-center">
                <CheckCircle2 className="w-10 h-10 text-[#0066FF]" strokeWidth={1.8} />
              </div>
              <h3 className="mt-6 text-2xl md:text-3xl font-bold text-white" style={{ fontFamily: "Poppins" }}>
                Mensaje enviado correctamente
              </h3>
              <p className="mt-3 text-slate-300 leading-relaxed max-w-md mx-auto">
                Te responderemos en menos de <strong className="text-white">24 horas</strong>. Si tu consulta es urgente, puedes llamarnos al{" "}
                <a href="tel:+34926032626" className="text-[#3B8AFF] hover:underline">926 032 626</a>.
              </p>
              <button
                type="button"
                onClick={resetForm}
                data-testid="contact-reset"
                className="mt-8 inline-flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 text-white font-medium px-6 py-3 rounded-xl transition-all"
              >
                Enviar otra consulta
              </button>
            </div>
          ) : (
            <form
              onSubmit={submit}
              className="bg-gradient-to-br from-[#1E3A5F]/40 to-[#0A1628]/40 backdrop-blur-md border border-white/10 rounded-3xl p-6 md:p-10"
              data-testid="contact-form"
              noValidate
            >
              <div className="grid sm:grid-cols-2 gap-4">
                <Field name="nombre" label="Nombre" value={form.nombre} onChange={handle} required />
                <Field name="empresa" label="Empresa" value={form.empresa} onChange={handle} required />
                <Field name="cargo" label="Cargo" value={form.cargo} onChange={handle} />
                <div>
                  <label className="text-xs uppercase tracking-[0.18em] text-slate-300 font-semibold">
                    Sector <span className="text-[#FF6B00]">*</span>
                  </label>
                  <select
                    name="sector"
                    value={form.sector}
                    onChange={handle}
                    required
                    data-testid="contact-sector-select"
                    className="mt-2 w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-[#0066FF] transition-colors"
                  >
                    <option value="" className="bg-[#0A1628]">Selecciona…</option>
                    <option value="Hidrocarburos" className="bg-[#0A1628]">Hidrocarburos</option>
                    <option value="Alcohol y Bebidas Derivadas" className="bg-[#0A1628]">Alcohol y Bebidas Derivadas</option>
                    <option value="Ambos" className="bg-[#0A1628]">Ambos</option>
                    <option value="Otro" className="bg-[#0A1628]">Otro</option>
                  </select>
                </div>
                <Field name="email" label="Email" type="email" value={form.email} onChange={handle} required />
                <Field name="telefono" label="Teléfono" type="tel" value={form.telefono} onChange={handle} />
              </div>

              <div className="mt-4">
                <label className="text-xs uppercase tracking-[0.18em] text-slate-300 font-semibold">
                  Mensaje <span className="text-[#FF6B00]">*</span>
                </label>
                <textarea
                  name="mensaje"
                  value={form.mensaje}
                  onChange={handle}
                  required
                  rows={5}
                  data-testid="contact-message-input"
                  className="mt-2 w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-[#0066FF] transition-colors resize-none"
                  placeholder="Describe brevemente tu operativa actual y qué necesitas…"
                />
              </div>

              <label className="mt-5 flex items-start gap-3 text-sm text-slate-300 cursor-pointer">
                <input
                  type="checkbox"
                  name="rgpd"
                  checked={form.rgpd}
                  onChange={handle}
                  data-testid="contact-rgpd-checkbox"
                  className="mt-1 w-4 h-4 accent-[#0066FF]"
                />
                <span>
                  He leído y acepto la{" "}
                  <a href="/politica-privacidad" className="text-[#0066FF] hover:underline">política de privacidad</a>
                  {" "}y el tratamiento de mis datos para el envío de esta consulta.
                </span>
              </label>

              <button
                type="submit"
                disabled={submitting}
                data-testid="contact-submit-button"
                className="mt-7 inline-flex items-center gap-3 bg-[#0066FF] hover:bg-[#0052CC] disabled:opacity-60 disabled:cursor-not-allowed text-white font-medium px-7 py-4 rounded-xl transition-all shadow-xl shadow-[#0066FF]/30 hover:shadow-[#0066FF]/50 hover:-translate-y-0.5"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Enviando…
                  </>
                ) : (
                  <>
                    Enviar consulta
                    <Send className="w-4 h-4" />
                  </>
                )}
              </button>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
};

const Field = ({ label, name, value, onChange, type = "text", required }) => (
  <div>
    <label className="text-xs uppercase tracking-[0.18em] text-slate-300 font-semibold">
      {label}{required && <span className="text-[#FF6B00]"> *</span>}
    </label>
    <input
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      required={required}
      data-testid={`contact-${name}-input`}
      className="mt-2 w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-[#0066FF] transition-colors"
    />
  </div>
);

export default Contact;
